import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'underscore';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { AddImageCrudAdComponent } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { useAssetLibraryImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';

export const AddImage = ({
  advertiserCustomerId,
  accountId,
  i18n,
  perfMarker,
  preferenceService,
  permissions,
  oDataConfig,
  suggestionState,
  suggestionPoller,
  fullUrl,
  urlPrefix,
  onUpdateUrlPrefix,
  onUpdateUrlValue,
  dateFormat,
  timeZone,
  activityLogger,
  maxUploadNum,
  displayImagePicker,
  setDisplayImagePicker,
  selectedMediaPanelSource,
  setSelectedMediaPanelSource,
  onNewImageSelected,
  isAADUser,
  creativeEditorProps,
  serverContext,
}) => {
  const [addImageChildPerfmarker] = useState(() => perfMarker.createChild('imagePicker'));
  const emptyFinalUrl = !fullUrl || fullUrl === `${urlPrefix}://`;

  const {
    imagePickerError,
    setImagePickerError,
    clearImagePickerError,
    uploadImage,
    processUploadedImage,
    minSize,
    addImageByUrl,
    getStockImages,
    getInvestImages,
    onSelectStockImage,
    onImageSelected,
    onImagesSelected,
    socialMediaDataSource,
    customMediaSourceConfig,
  } = useAssetLibraryImagePicker({
    cid: advertiserCustomerId.toString(),
    aid: accountId.toString(),
    oDataConfig,
    onNewImageSelected,
    i18n,
    permissions,
    isDisplayAds: true,
  });

  const { suggestion = {} } = suggestionState;

  return (
    <AddImageCrudAdComponent
      isOpen={displayImagePicker}
      setIsOpen={(newOpen) => {
          setDisplayImagePicker(newOpen);
          if (!newOpen) {
            clearImagePickerError();
          }
        }}
      uploadImage={uploadImage}
      processUploadedImage={processUploadedImage}
      minSize={minSize}
      maxUploadNum={maxUploadNum}
      imageExperienceProps={{
          suggestion,
          suggestionPoller,
          finalUrl: emptyFinalUrl ? '' : fullUrl,
          finalUrlProtocol: `${urlPrefix}://`,
          onUpdateFinalUrl: onUpdateUrlValue,
          onUpdateFinalUrlPrefix: onUpdateUrlPrefix,
          isMultimediaAd: false,
          imageOption: constants.defaultImageOption,
          isDisplayAds: true,
        }}
      addImageByUrl={addImageByUrl}
      managedViewSelectionState={selectedMediaPanelSource}
      updateManagedViewSelectionState={setSelectedMediaPanelSource}
      stockImageConfig={{
          getStockImages,
          onSelectStockImage,
        }}
      investImageConfig={{
        getInvestImages,
      }}
      socialImageConfig={{
          socialMediaDataSource,
          activityLogger,
        }}
      assetLibraryConfig={{
          cid: advertiserCustomerId.toString(),
          aid: accountId.toString(),
          oDataConfig,
          dateFormat,
          timeZone,
          preferencesService: preferenceService,
          activityLogger,
          onImageSelected,
          onImagesSelected,
          onImageAddError: e => setImagePickerError(e),
          enableStockMultipleSelection: true,
          enableInvestMultipleSelection: true,
          enableWebsiteMultipleSelection: true,
        }}
      i18n={i18n}
      cid={advertiserCustomerId.toString()}
      aid={accountId.toString()}
      errorMessage={imagePickerError}
      imageType={null}
      customMediaSourceConfig={customMediaSourceConfig}
      perfMarker={addImageChildPerfmarker}
      permissions={permissions}
      isAADUser={isAADUser}
      creativeEditorProps={creativeEditorProps}
      serverContext={serverContext}
    />
  );
};

AddImage.propTypes = {
  advertiserCustomerId: PropTypes.number.isRequired,
  accountId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
    }),
  }),
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,
  suggestionState: PropTypes.shape({
    suggestion: PropTypes.shape({}),
    carousel: PropTypes.shape({}),
  }).isRequired,
  suggestionPoller: PropTypes.shape({}).isRequired,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  fullUrl: PropTypes.string,
  urlPrefix: PropTypes.string,
  onUpdateUrlPrefix: PropTypes.func,
  onUpdateUrlValue: PropTypes.func,
  dateFormat: PropTypes.string,
  timeZone: PropTypes.number,
  activityLogger: PropTypes.shape({}),
  maxUploadNum: PropTypes.number,
  displayImagePicker: PropTypes.bool,
  setDisplayImagePicker: PropTypes.func.isRequired,
  selectedMediaPanelSource: PropTypes.string.isRequired,
  setSelectedMediaPanelSource: PropTypes.func.isRequired,
  onNewImageSelected: PropTypes.func.isRequired,
  isAADUser: PropTypes.bool,
  creativeEditorProps: PropTypes.shape({}),
  assetLibraryConfigs: PropTypes.shape({
    imagePickerError: PropTypes.string,
    setImagePickerError: PropTypes.func,
    clearImagePickerError: PropTypes.func,
    uploadImage: PropTypes.func,
    processUploadedImage: PropTypes.func,
    minSize: PropTypes.number,
    addImageByUrl: PropTypes.func,
    getStockImages: PropTypes.func,
    getInvestImages: PropTypes.func,
    onSelectStockImage: PropTypes.func,
    onImageSelected: PropTypes.func,
    onImagesSelected: PropTypes.func,
    socialMediaDataSource: PropTypes.shape({}),
    customMediaSourceConfig: PropTypes.shape({}),
  }).isRequired,
};

AddImage.defaultProps = {
  permissions: {},
  adCreatorScenario: undefined,
  fullUrl: '',
  urlPrefix: '',
  onUpdateUrlPrefix: noop,
  onUpdateUrlValue: noop,
  dateFormat: 'M/d/yyyy',
  timeZone: 71,
  activityLogger: {},
  displayImagePicker: false,
  maxUploadNum: 1,
  isAADUser: false,
  creativeEditorProps: undefined,
};
